import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { CheckCircle2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { StarRatingDisplay, StarRatingInput } from "./StarRating";

function fmtDate(s) {
  try {
    return new Date(s).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "";
  }
}

function ReviewItem({ r }) {
  return (
    <article
      data-testid={`review-item-${r.id}`}
      className="border border-[#E5EAE5] rounded-2xl p-5 bg-white"
    >
      <header className="flex items-start justify-between gap-4 mb-2">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <StarRatingDisplay value={r.rating} size={16} testId={`review-stars-${r.id}`} />
            <span className="text-sm font-semibold text-[#1A2E1A]">{r.rating}/5</span>
            {r.is_verified_purchase && (
              <Badge className="bg-[#7AC143] hover:bg-[#7AC143] text-white rounded-full text-xs gap-1 px-2 py-0.5">
                <ShieldCheck className="w-3 h-3" /> Achat vérifié
              </Badge>
            )}
          </div>
          <h4 className="font-bold text-[#1A2E1A] mt-2 leading-snug">{r.title}</h4>
        </div>
        <span className="text-xs text-[#4B634B] whitespace-nowrap">{fmtDate(r.created_at)}</span>
      </header>
      <p className="text-[#4B634B] leading-relaxed whitespace-pre-line">{r.comment}</p>
      <p className="text-xs text-[#9CB39C] mt-3">
        — {r.user_name || "Client Herbalife"}
      </p>
    </article>
  );
}

function ReviewForm({ productId, existing, onSubmitted, onCancel }) {
  const [rating, setRating] = useState(existing?.rating || 0);
  const [title, setTitle] = useState(existing?.title || "");
  const [comment, setComment] = useState(existing?.comment || "");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (rating < 1 || rating > 5) return toast.error("Choisissez une note entre 1 et 5 étoiles");
    if (!title.trim()) return toast.error("Le titre est obligatoire");
    if (comment.trim().length < 10) return toast.error("Le commentaire doit faire au moins 10 caractères");
    setBusy(true);
    try {
      const r = await api.post(`/products/${productId}/reviews`, { rating, title: title.trim(), comment: comment.trim() });
      toast.success(r.data.message);
      onSubmitted(r.data.review);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'envoi de l'avis");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4 border border-[#E5EAE5] rounded-2xl p-5 bg-[#F9FAF6]" data-testid="review-form">
      <div>
        <label className="block text-sm font-semibold text-[#1A2E1A] mb-2">Votre note</label>
        <StarRatingInput value={rating} onChange={setRating} testId="review-form-rating" />
      </div>
      <div>
        <label className="block text-sm font-semibold text-[#1A2E1A] mb-2">Titre</label>
        <Input
          data-testid="review-form-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Résumez votre expérience en quelques mots"
          maxLength={120}
        />
      </div>
      <div>
        <label className="block text-sm font-semibold text-[#1A2E1A] mb-2">Votre avis</label>
        <Textarea
          data-testid="review-form-comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Partagez votre expérience avec ce produit (texture, goût, résultats, etc.)…"
          rows={5}
          maxLength={2000}
        />
        <p className="text-xs text-[#4B634B] mt-1">{comment.length}/2000 caractères · minimum 10</p>
      </div>
      <div className="flex flex-wrap items-center justify-end gap-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} data-testid="review-form-cancel">
            Annuler
          </Button>
        )}
        <Button
          type="submit"
          className="bg-[#7AC143] hover:bg-[#67BC2A] text-white"
          disabled={busy}
          data-testid="review-form-submit"
        >
          {busy ? "Envoi…" : existing ? "Mettre à jour mon avis" : "Publier mon avis"}
        </Button>
      </div>
    </form>
  );
}

function Breakdown({ summary }) {
  if (!summary || summary.count === 0) return null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-x-8 gap-y-2 items-center" data-testid="review-breakdown">
      <div className="flex items-center gap-3">
        <div className="text-4xl font-black text-[#1A2E1A]">{summary.average.toFixed(1)}</div>
        <div className="flex flex-col">
          <StarRatingDisplay value={summary.average} size={18} />
          <span className="text-xs text-[#4B634B] mt-1">
            {summary.count} avis vérifié{summary.count > 1 ? "s" : ""}
          </span>
        </div>
      </div>
      <div className="space-y-1.5">
        {[5, 4, 3, 2, 1].map((star) => {
          const c = summary.breakdown?.[String(star)] || 0;
          const pct = summary.count > 0 ? (c / summary.count) * 100 : 0;
          return (
            <div key={star} className="flex items-center gap-3 text-xs text-[#4B634B]">
              <span className="w-8 text-right">{star}★</span>
              <div className="flex-1 h-1.5 bg-[#E5EAE5] rounded-full overflow-hidden">
                <div className="h-full bg-[#FACC15]" style={{ width: `${pct}%` }} />
              </div>
              <span className="w-8 tabular-nums">{c}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ReviewsSection({ productId }) {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [eligibility, setEligibility] = useState(null); // { can_review, has_purchased, existing_review }
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    const [s, r] = await Promise.all([
      api.get(`/products/${productId}/review-summary`).then((x) => x.data),
      api.get(`/products/${productId}/reviews`).then((x) => x.data),
    ]);
    setSummary(s);
    setReviews(r);
    if (user) {
      try {
        const e = await api.get(`/products/${productId}/can-review`).then((x) => x.data);
        setEligibility(e);
      } catch {
        setEligibility(null);
      }
    } else {
      setEligibility(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, user?.id]);

  if (loading) {
    return (
      <section data-testid="reviews-loading" className="text-sm text-[#4B634B] py-8">
        Chargement des avis…
      </section>
    );
  }

  const existing = eligibility?.existing_review;

  return (
    <section className="mt-16 pt-12 border-t border-[#E5EAE5]" data-testid="product-reviews">
      <header className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs font-bold tracking-[0.3em] uppercase text-[#7AC143] mb-2">Avis clients</div>
          <h2 className="text-3xl font-black tracking-tight text-[#1A2E1A]">
            {summary?.count > 0 ? "Ils ont aimé ce produit" : "Soyez le premier à donner votre avis"}
          </h2>
        </div>
        {summary?.count > 0 && (
          <div className="text-right">
            <StarRatingDisplay value={summary.average} size={22} testId="reviews-summary-stars" />
            <p className="text-sm text-[#4B634B] mt-1">
              <span className="font-bold text-[#1A2E1A]">{summary.average.toFixed(1)}</span>{" "}
              · {summary.count} avis
            </p>
          </div>
        )}
      </header>

      {summary?.count > 0 && (
        <div className="mb-10 bg-[#F9FAF6] border border-[#E5EAE5] rounded-2xl p-5">
          <Breakdown summary={summary} />
        </div>
      )}

      {/* Eligibility / form area */}
      <div className="mb-10">
        {!user && (
          <div data-testid="reviews-cta-login" className="bg-[#F2F5F0] border border-[#E5EAE5] rounded-2xl p-5 text-sm text-[#4B634B]">
            <span className="text-[#1A2E1A] font-semibold">Connectez-vous</span> pour laisser un avis. Seuls les clients ayant acheté ce produit peuvent publier un avis vérifié.
          </div>
        )}

        {user && eligibility && !eligibility.has_purchased && (
          <div data-testid="reviews-cta-not-purchased" className="bg-[#F2F5F0] border border-[#E5EAE5] rounded-2xl p-5 text-sm text-[#4B634B]">
            <span className="text-[#1A2E1A] font-semibold">Achat vérifié uniquement</span> — vous pourrez laisser votre avis sur ce produit dès que vous l'aurez commandé.
          </div>
        )}

        {user && eligibility?.has_purchased && existing && !showForm && (
          <div data-testid="reviews-existing" className="bg-[#F9FAF6] border border-[#E5EAE5] rounded-2xl p-5">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-semibold text-[#1A2E1A]">Votre avis</span>
                  {existing.status === "pending" && (
                    <Badge className="bg-[#FACC15] text-[#1A2E1A] hover:bg-[#FACC15] rounded-full text-xs">
                      En attente de validation
                    </Badge>
                  )}
                  {existing.status === "approved" && (
                    <Badge className="bg-[#7AC143] hover:bg-[#7AC143] text-white rounded-full text-xs gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Publié
                    </Badge>
                  )}
                  {existing.status === "rejected" && (
                    <Badge className="bg-red-500 hover:bg-red-500 text-white rounded-full text-xs">
                      Rejeté par la modération
                    </Badge>
                  )}
                </div>
                <StarRatingDisplay value={existing.rating} size={16} />
                <h4 className="font-bold text-[#1A2E1A] mt-2">{existing.title}</h4>
                <p className="text-sm text-[#4B634B] mt-1 whitespace-pre-line">{existing.comment}</p>
              </div>
              <Button
                variant="outline"
                onClick={() => setShowForm(true)}
                data-testid="reviews-edit-mine"
              >
                Modifier mon avis
              </Button>
            </div>
          </div>
        )}

        {user && eligibility?.has_purchased && (showForm || !existing) && (
          <ReviewForm
            productId={productId}
            existing={existing}
            onSubmitted={async () => {
              setShowForm(false);
              await refresh();
            }}
            onCancel={existing ? () => setShowForm(false) : undefined}
          />
        )}
      </div>

      {/* List */}
      {reviews.length === 0 ? (
        <p className="text-sm text-[#4B634B]">Aucun avis publié pour le moment.</p>
      ) : (
        <div className="space-y-4" data-testid="reviews-list">
          {reviews.map((r) => (
            <ReviewItem key={r.id} r={r} />
          ))}
        </div>
      )}
    </section>
  );
}
